import React, { useState } from 'react'
import { Code } from 'lucide-react'
import { genId } from '../lib/id'
import { readFileAsText } from '../lib/fileRead'
import FileDropzone from '../components/FileDropzone'
import type { HtmlCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

const HTML_ACCEPT = { 'text/html': ['.html', '.htm'] }

// Lý thuyết tương tác: GV dán/upload HTML (kèm CSS/JS). Chạy trong iframe sandbox
// để cô lập hoàn toàn khỏi ứng dụng chính — đây là cách "biên dịch" an toàn.
const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as HtmlCard
  const [busy, setBusy] = useState(false)

  const handleFile = async (file: File) => {
    setBusy(true)
    try {
      const text = await readFileAsText(file)
      onChange({ htmlSource: text, title: (card.title || file.name.replace(/\.html?$/i, '')) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <FileDropzone
        accept={HTML_ACCEPT}
        onFile={handleFile}
        busy={busy}
        label="Kéo-thả file .html để nạp nội dung tương tác"
        hint="Hỗ trợ HTML/CSS/JS, chạy cô lập trong khung an toàn"
      />
      <label className="text-xs font-bold text-gray-500 uppercase">Mã HTML (có thể chỉnh tay)</label>
      <textarea
        className="w-full min-h-[180px] p-3 border border-gray-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-200"
        value={c.htmlSource}
        onChange={(e) => onChange({ htmlSource: e.target.value })}
        placeholder="<div>... nội dung tương tác của bạn ...</div>"
      />
      <div className="flex items-center gap-2">
        <label className="text-xs font-bold text-gray-500 uppercase">Chiều cao (px)</label>
        <input
          type="number"
          className="w-28 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
          value={c.height ?? 400}
          onChange={(e) => onChange({ height: Number(e.target.value) || 400 })}
        />
      </div>
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as HtmlCard
  if (!c.htmlSource) {
    return (
      <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">
        Chưa có nội dung HTML.
      </div>
    )
  }
  return (
    <iframe
      title={c.title || 'Nội dung HTML tương tác'}
      srcDoc={c.htmlSource}
      sandbox="allow-scripts allow-same-origin allow-popups"
      className="w-full rounded-lg border border-gray-200 bg-white"
      style={{ height: c.height ?? 400 }}
    />
  )
}

export const htmlCardDef: CardDef = {
  type: 'html',
  label: 'HTML tương tác',
  description: 'Dán HTML/CSS/JS, chạy cô lập',
  icon: Code,
  color: 'bg-cyan-50 text-cyan-600',
  createDefault: (): HtmlCard => ({ id: genId(), type: 'html', htmlSource: '', height: 400 }),
  Editor,
  Preview,
}
